/**
 * Galaxy — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_motifs`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const galaxyPack: ToilePack = {
    slug: 'galaxy',
    category: 'motif',
    name: 'Galaxy',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 768, h: 1344},
    readings: [
        {key: 'spiral', label: 'Spiral galaxy', subject: 'a spiral galaxy seen face-on, arms of blue and white stars curving out of a bright golden core'},
        {key: 'nebula', label: 'Nebula', subject: 'a nebula of pink, teal and violet gas with new stars burning inside it and dark pillars of dust'},
        {key: 'milkyway', label: 'Milky Way', subject: 'the Milky Way arching over a black mountain ridge and a still lake that doubles the band of stars'},
    ],
    styles: [
        {key: 'watercolor', label: 'Watercolor', style: 'loose watercolour, wet bleeds of purple and teal, salt-bloomed stars and a fine white splatter', palette: 'Violet, teal and indigo washes with bare white paper showing through.', framing: 'The wash runs edge to edge with no frame drawn around it.'},
        {key: 'painted', label: 'Painted', style: 'a painted space scene, soft luminous glow built out of deep black with fine star detail over it', palette: 'Deep black with cobalt, magenta and a warm white core light.', framing: 'The picture fills the tall frame, the glow gathering in the lower third.'},
        {key: 'flat', label: 'Flat poster', style: 'a flat vector space poster, the forms simplified into bold clean shapes with hard edges and no outlines', palette: 'A limited palette of navy, coral, teal and cream.', framing: 'The shapes sit full-bleed on a solid navy ground.'},
    ],
    directions: ['a phone wallpaper', 'a nursery print', 'a tattoo'],
    directionHint: 'try: a phone wallpaper',
    seo: {
        title: 'Galaxy designs: spiral, nebula, Milky Way',
        metaDescription: 'Nine galaxy designs — a face-on spiral, a pink-teal nebula, the Milky Way over a mountain — as watercolor, painted and flat.',
        h1: 'Galaxy designs: spiral galaxy, nebula, Milky Way',
        tagline: 'Nine galaxy scenes, three arrangements, three styles each — the star field is the texture every column makes.',
        intro: 'The galaxy is the phone wallpaper and the nebula print, deep on every wall-art site. These nine set a spiral galaxy face-on, a nebula of pink and teal gas, and the Milky Way over a mountain — against watercolor, painted and flat. The star field is the texture every column has to produce. Ask for a different cosmic subject and the same three styles carry it.',
        useCases: [
            'a phone or desktop wallpaper crop',
            'a bedroom or teen room wall print',
            'a coloring page',
            'a science-classroom decor piece',
            'a gift print for a space enthusiast',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each design is drawn at A3 proportions with an even margin, so it prints clean at A3 or scales to A4.'},
            {q: 'Can I request a different cosmic subject?', a: 'Yes — the direction field takes any subject, such as a specific constellation or a black hole, and the same three styles render it.'},
            {q: "What's the difference between the three styles?", a: 'Watercolor is soft washes with visible paper texture; painted is thick, blended brushwork; flat is clean solid shapes with a limited palette.'},
            {q: 'Do these work as a phone wallpaper?', a: 'Yes — each design is composed to crop cleanly to a vertical phone screen without losing the star field.'},
        ],
    },
}
